import type { Href } from "expo-router";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import {
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
  type ViewStyle,
} from "react-native";
import { COLORS, FONTS } from "../lib/brand";

import { track } from "../lib/analytics";
import { MarketingHead } from "../components/MarketingHead";
import { META_PRICING } from "../lib/marketingMeta";

const FOREST = COLORS.FOREST.hex;
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const AMBER = COLORS.AMBER.hex;
const SAND = COLORS.SAND.hex;
const LINEN = COLORS.LINEN.hex;

const TEXT_DIM = "rgba(237,232,223,0.62)";
const TEXT_FAINT = "rgba(237,232,223,0.40)";
const BORDER = "rgba(181,135,58,0.18)";
const BORDER_SOFT = "rgba(237,232,223,0.08)";
const PAGE_BG = COLORS.FOREST_DEEPER.hex;

const BOOKINGS_URL = "https://outlook.office.com/book/CoravuIntake@Coravu.com/";

/** Discovery-call CTA dispatcher. Tags the click with the placement
 * `source` so analytics can answer "which CTA actually converts" once
 * we have N=N>0 signal. Fire-and-forget — analytics is a graceful
 * no-op when the env key is missing. Open in a new tab so the user
 * keeps the marketing context behind them. */
function openBookings(source: string) {
  track("discovery_call_cta_clicked", { source });
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.open(BOOKINGS_URL, "_blank", "noopener,noreferrer");
  } else {
    void Linking.openURL(BOOKINGS_URL);
  }
}

type LicensingTier = {
  label: string;
  perStudent: string;
  perSubmission: string;
};

const LICENSING_TIERS: LicensingTier[] = [
  { label: "Up to 100 students", perStudent: "$4.99", perSubmission: "$1.50" },
  { label: "Up to 250 students", perStudent: "$3.75", perSubmission: "$1.35" },
  { label: "Up to 500 students", perStudent: "$3.50", perSubmission: "$1.20" },
  { label: "National / unlimited", perStudent: "$3.00", perSubmission: "$1.00" },
];

const FAQ: Array<{ q: string; a: string }> = [
  {
    q: "What's included in the licensing fee?",
    a:
      "Per-student and per-submission fees cover platform access for the duration of one competition: hosted case page with your branding, eligibility filters, team formation, deliverable submission, judging tools, and the live leaderboard. You manage the case content; we provide the rails.",
  },
  {
    q: "How do prize pools work?",
    a:
      "Your prize pool is administered separately from the licensing fee and charged to you in full prior to the competition opening. Coravu disburses prize money directly to winning students with proper 1099 handling for any individual share over $600.",
  },
  {
    q: "Can I run a competition without recruitment?",
    a:
      "Yes. Licensing is the right tier when you want the platform but not the operational lift. Recruitment Management is for clients who want Coravu to design the case, run the program, judge the work, and surface the talent — full-service execution.",
  },
  {
    q: "Is pricing in USD?",
    a: "Yes. All fees are quoted and billed in US Dollars.",
  },
];

export default function PricingScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 900;
  const pad = wide ? 64 : 22;

  // Two-up grid for licensing tiers above 720; single column below.
  // Same threshold the landing's For Companies section uses, so the
  // breakpoints feel consistent as you scroll between pages.
  const twoUp = width >= 720;

  useEffect(() => {
    track("pricing_page_viewed");
  }, []);

  return (
    <>
      <MarketingHead meta={META_PRICING} />
      <ScrollView
        style={{ flex: 1, backgroundColor: PAGE_BG }}
        contentContainerStyle={{ paddingBottom: 64 }}
      >
        <View style={{ paddingHorizontal: pad, paddingTop: 32, alignSelf: "center", width: "100%", maxWidth: 1080 }}>
          <Pressable
            onPress={() => router.replace("/" as Href)}
            style={[
              { alignSelf: "flex-start", paddingVertical: 4, marginBottom: 12 },
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyMedium,
                fontSize: 11.5,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: TEXT_FAINT,
              }}
            >
              ← Coravu
            </Text>
          </Pressable>

          {/* HERO */}
          <View style={{ marginTop: wide ? 28 : 16 }}>
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 2.4,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              Pricing
            </Text>
            <Text
              style={{
                marginTop: 12,
                fontFamily: FONTS.display,
                fontSize: wide ? 64 : 44,
                lineHeight: wide ? 68 : 48,
                color: LINEN,
                letterSpacing: -1,
              }}
            >
              Pay only for what you{" "}
              <Text style={{ fontStyle: "italic", color: AMBER }}>run</Text>.
            </Text>
            <Text
              style={{
                marginTop: 16,
                fontFamily: FONTS.bodyLight,
                fontSize: wide ? 16 : 14,
                lineHeight: wide ? 26 : 23,
                color: TEXT_DIM,
                maxWidth: 720,
              }}
            >
              All competition hosts pay a per-competition licensing fee. Recruitment Management
              adds full-service execution. No subscriptions, no minimums, no surprise charges.
            </Text>
          </View>

          {/* FOUNDING-CLIENT BANNER */}
          <View
            style={{
              marginTop: 36,
              paddingHorizontal: wide ? 28 : 22,
              paddingVertical: wide ? 26 : 22,
              borderRadius: 8,
              backgroundColor: FOREST_DEEP,
              borderWidth: 1,
              borderColor: "rgba(181,135,58,0.45)",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 2.4,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              Phase 1 — Founding Client Program
            </Text>
            <Text
              style={{
                marginTop: 10,
                fontFamily: FONTS.display,
                fontSize: wide ? 32 : 26,
                lineHeight: wide ? 38 : 32,
                color: LINEN,
              }}
            >
              25% off your first two competitions.
            </Text>
            <Text
              style={{
                marginTop: 10,
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: 22,
                color: TEXT_DIM,
                maxWidth: 720,
              }}
            >
              Sign a Letter of Intent during Phase 1 and receive a founding-client discount on
              all licensing and Recruitment Management fees for your first two competitions.
            </Text>
            <View style={{ marginTop: 16 }}>
              <PrimaryCta
                label="Book a discovery call →"
                onPress={() => openBookings("pricing_founding_banner")}
              />
            </View>
          </View>

          {/* LICENSING SECTION */}
          <SectionHeader title="Licensing" />
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.bodyLight,
              fontSize: 14,
              lineHeight: 22,
              color: TEXT_DIM,
              maxWidth: 720,
            }}
          >
            Run your own competition on the platform. SaaS-style per-competition fees. No
            operational involvement from Coravu — you set up the case, manage students, and pull
            the data.
          </Text>

          <View
            style={{
              marginTop: 24,
              flexDirection: twoUp ? "row" : "column",
              flexWrap: twoUp ? "wrap" : "nowrap",
              gap: 14,
            }}
          >
            {LICENSING_TIERS.map((tier) => (
              <LicensingCard key={tier.label} tier={tier} twoUp={twoUp} />
            ))}
          </View>

          <Text
            style={{
              marginTop: 18,
              fontFamily: FONTS.body,
              fontStyle: "italic",
              fontSize: 12.5,
              lineHeight: 19,
              color: TEXT_FAINT,
              maxWidth: 720,
            }}
          >
            Your competition's prize pool is administered separately and charged to you in full
            prior to the competition listing. Coravu disburses prize money directly to winning
            students.
          </Text>

          {/* RECRUITMENT MANAGEMENT */}
          <SectionHeader title="Recruitment Management" />
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.bodyLight,
              fontSize: 14,
              lineHeight: 22,
              color: TEXT_DIM,
              maxWidth: 760,
            }}
          >
            We run the entire competition for you — venue coordination, student outreach,
            deliverable management, judging, and competition-day staffing. Includes licensing.
            Custom-priced based on scope and student volume.
          </Text>

          <View
            style={{
              marginTop: 22,
              paddingHorizontal: wide ? 32 : 22,
              paddingVertical: wide ? 30 : 24,
              borderRadius: 8,
              backgroundColor: FOREST_DEEP,
              borderWidth: 1,
              borderColor: BORDER,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 2.4,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              Full-service
            </Text>
            <Text
              style={{
                marginTop: 10,
                fontFamily: FONTS.display,
                fontSize: wide ? 36 : 28,
                lineHeight: wide ? 42 : 34,
                color: LINEN,
              }}
            >
              Custom pricing
            </Text>
            <Text
              style={{
                marginTop: 10,
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: 22,
                color: TEXT_DIM,
                maxWidth: 720,
              }}
            >
              Pricing depends on student volume, judging panel size, venue requirements, and
              prize pool. We'll scope it on a 30-minute discovery call.
            </Text>
            <View style={{ marginTop: 18 }}>
              <PrimaryCta
                label="Book a discovery call →"
                onPress={() => openBookings("pricing_rm_tier")}
                large
              />
            </View>
          </View>

          {/* STUDENT ORG (FREE) */}
          <SectionHeader title="Student organizations — free" />
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.bodyLight,
              fontSize: 14,
              lineHeight: 22,
              color: TEXT_DIM,
              maxWidth: 760,
            }}
          >
            Coravu's platform is free of charge to official student organizations running
            internal competitions with no external corporate recruitment involved. We want every
            student-led competition on Coravu so the next generation of operators learns the
            format.
          </Text>
          <View
            style={{
              marginTop: 18,
              paddingHorizontal: wide ? 22 : 18,
              paddingVertical: wide ? 18 : 16,
              borderRadius: 6,
              backgroundColor: "rgba(255,255,255,0.02)",
              borderWidth: 1,
              borderColor: BORDER_SOFT,
              maxWidth: 720,
            }}
          >
            <Text style={{ fontFamily: FONTS.display, fontSize: 22, color: LINEN }}>
              $0 per competition for student organizations
            </Text>
            <Text
              style={{
                marginTop: 8,
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 20,
                color: TEXT_DIM,
              }}
            >
              Standard licensing fees apply only when a corporate partner is involved in a
              recruiting capacity. Email{" "}
              <Text style={{ color: AMBER, fontFamily: FONTS.bodyMedium }}>hello@coravu.com</Text>{" "}
              to set up your org's account.
            </Text>
          </View>

          {/* FAQ */}
          <SectionHeader title="Common questions" />
          <View
            style={{
              marginTop: 14,
              borderRadius: 6,
              backgroundColor: FOREST_DEEP,
              borderWidth: 1,
              borderColor: BORDER_SOFT,
              maxWidth: 760,
              overflow: "hidden",
            }}
          >
            {FAQ.map((item, i) => (
              <FaqRow key={item.q} item={item} isLast={i === FAQ.length - 1} />
            ))}
          </View>

          {/* FINAL CTA */}
          <View
            style={{
              marginTop: 48,
              paddingHorizontal: wide ? 32 : 22,
              paddingVertical: wide ? 36 : 28,
              borderRadius: 8,
              backgroundColor: "rgba(181,135,58,0.06)",
              borderWidth: 1,
              borderColor: "rgba(181,135,58,0.32)",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.display,
                fontSize: wide ? 32 : 26,
                lineHeight: wide ? 38 : 32,
                color: LINEN,
                textAlign: "center",
              }}
            >
              Ready to run your first competition?
            </Text>
            <View style={{ marginTop: 16 }}>
              <PrimaryCta
                label="Book a discovery call →"
                onPress={() => openBookings("pricing_footer")}
                large
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Text
      style={{
        marginTop: 56,
        fontFamily: FONTS.display,
        fontSize: 32,
        lineHeight: 38,
        color: LINEN,
        letterSpacing: -0.5,
      }}
    >
      {title}
    </Text>
  );
}

function LicensingCard({ tier, twoUp }: { tier: LicensingTier; twoUp: boolean }) {
  return (
    <View
      style={{
        flexBasis: twoUp ? "calc(50% - 7px)" as unknown as number : "100%",
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        paddingHorizontal: 22,
        paddingVertical: 22,
        borderRadius: 8,
        backgroundColor: FOREST_DEEP,
        borderWidth: 1,
        borderColor: BORDER_SOFT,
      } as ViewStyle}
    >
      <Text
        style={{
          fontFamily: FONTS.displayMedium,
          fontSize: 22,
          color: LINEN,
        }}
      >
        {tier.label}
      </Text>
      <View style={{ marginTop: 14, flexDirection: "row", alignItems: "baseline", gap: 6 }}>
        <Text
          style={{
            fontFamily: FONTS.displayMedium,
            fontSize: 36,
            lineHeight: 36,
            color: AMBER,
          }}
        >
          {tier.perStudent}
        </Text>
        <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: TEXT_DIM }}>
          per student
        </Text>
      </View>
      <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 13, color: TEXT_DIM }}>
        {tier.perSubmission} per submitted deliverable
      </Text>
      <Text
        style={{
          marginTop: 14,
          fontFamily: FONTS.bodyMedium,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: TEXT_FAINT,
        }}
      >
        Per competition
      </Text>
    </View>
  );
}

function FaqRow({ item, isLast }: { item: { q: string; a: string }; isLast: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <Pressable
      onPress={() => setOpen((v) => !v)}
      style={[
        {
          paddingHorizontal: 18,
          paddingVertical: 16,
          borderBottomWidth: isLast ? 0 : 1,
          borderBottomColor: BORDER_SOFT,
        },
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
        <Text style={{ flex: 1, fontFamily: FONTS.bodyMedium, fontSize: 14, color: LINEN }}>
          {item.q}
        </Text>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 14,
            color: AMBER,
            width: 14,
            textAlign: "center",
          }}
        >
          {open ? "−" : "+"}
        </Text>
      </View>
      {open ? (
        <Text
          style={{
            marginTop: 10,
            fontFamily: FONTS.body,
            fontSize: 13,
            lineHeight: 21,
            color: TEXT_DIM,
          }}
        >
          {item.a}
        </Text>
      ) : null}
    </Pressable>
  );
}

function PrimaryCta({
  label,
  onPress,
  large,
}: {
  label: string;
  onPress: () => void;
  large?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }) => [
        {
          alignSelf: "flex-start",
          paddingHorizontal: large ? 22 : 18,
          paddingVertical: large ? 13 : 11,
          borderRadius: 4,
          backgroundColor: AMBER,
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        Platform.OS === "web" && hovered
          ? ({ backgroundColor: COLORS.AMBER_WARM.hex, transform: [{ translateY: -1 }] } as ViewStyle)
          : null,
        pressed ? ({ opacity: 0.92 } as ViewStyle) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: large ? 12 : 11.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: FOREST,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

// `SAND` is referenced for future palette extensions; export is kept
// alive so the const isn't tree-shaken accidentally.
void SAND;
